import { Alert, Button, Input, Select } from 'antd';
import { Formik } from 'formik';
import React from 'react';
import { useSelector } from 'react-redux';

import FullPageLoader from '../../../foundation/components/full_page_loader/FullPageLoader';
import Modal from '../../../foundation/components/modal/Modal';
import { useVariantAccess } from '../hooks/useVariantAccess';
import { selectVariantLock } from '../redux';
import { VariantSample } from '../redux';
import { zygosityValidationSchema } from '../validation_schemas/zygosity_validation_schema';
import { useZygosityModalLogic } from './hooks';

type ZygosityModalProps = {
  onClose: () => void;
  variantSamples: VariantSample[];
  selectedSample: VariantSample;
};

const ZygosityModal = ({
  onClose,
  variantSamples,
  selectedSample,
}: ZygosityModalProps) => {
  const { isReadOnly, canInteract } = useVariantAccess();
  const lock = useSelector(selectVariantLock);

  // All logic extracted to custom hook for separation of concerns
  const {
    isZygosityModalLoading,
    zygosityOptions,
    modalOpts,
    initialValues,
    getIsSaveDisabled,
    handleFormSubmit,
  } = useZygosityModalLogic({ onClose, variantSamples, selectedSample });

  return (
    <Modal {...modalOpts} className="h-proband-modal">
      {isZygosityModalLoading && <FullPageLoader />}

      {isReadOnly && lock && (
        <Alert
          message="Read-Only Mode"
          description={`This variant is currently locked by ${lock.locked_by} and cannot be edited.`}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Formik
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
        validationSchema={zygosityValidationSchema}
      >
        {({
          errors,
          values,
          handleSubmit,
          handleBlur,
          isValid,
          dirty,
          setFieldValue,
        }) => {
          const isSaveDisabled = getIsSaveDisabled(isValid, dirty);

          return (
            <form onSubmit={handleSubmit}>
              <div className="c-form-field">
                <div className="c-form-field__label">Sample ID:</div>
                <div className="c-form-field__wrapper">
                  <Input
                    name="sample_id"
                    type="text"
                    value={selectedSample.sample_external_id}
                    placeholder="Sample ID"
                    disabled
                    className="h-proband-modal__input-field-disabled-highlight"
                  />
                </div>
              </div>

              <div className="c-form-field">
                <div className="c-form-field__label">Zygosity:</div>
                <div className="c-form-field__wrapper">
                  <Select
                    value={values.zygosity}
                    onChange={(v) => setFieldValue('zygosity', v)}
                    onBlur={handleBlur}
                    className="h-proband-modal__select-field"
                    options={zygosityOptions}
                    placeholder="Choose variant zygosity for sample"
                    disabled={!canInteract}
                    status={!canInteract ? 'error' : undefined}
                  />
                  <div className="c-form-field__error">
                    {errors.zygosity ? errors.zygosity : undefined}
                  </div>
                </div>
              </div>

              <div className="c-form__btn-wrapper h-proband-modal__form-btn-wrapper">
                <Button
                  type="primary"
                  htmlType="submit"
                  className="h-proband-modal__form-submit-btn"
                  disabled={isSaveDisabled}
                >
                  Save changes
                </Button>
              </div>
            </form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default ZygosityModal;
